// © Thibault Sabathier, 2026. Tous droits réservés.
// frontend/src/views/jobs/searches/MatchesModal.js
//
// Offres qui correspondent à UNE de mes recherches d'opportunité (lot 4). Chaque offre dit POURQUOI
// elle est proposée (contrat, secteur, lieu) : aucun score. Une offre dont le secteur ou le lieu
// n'est pas renseigné le dit aussi, au lieu de passer pour une concordance.
//
// « Me prévenir » est une action EXPLICITE : rien ne part sans ce clic, et une offre n'est
// signalée qu'une fois. Le serveur vérifie le consentement et la propriété à chaque appel.

import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  CModal,
  CModalHeader,
  CModalTitle,
  CModalBody,
  CModalFooter,
  CButton,
  CBadge,
  CAlert,
  CSpinner,
} from '@coreui/react'
import { fetchMatches, notifyMatches } from '../../../utils/memberSearchesApi'
import { describeReasons, parseApiError, errorKeyFor } from '../../../utils/memberSearches'
import SearchPager from './SearchPager'
import './searches.scss'

/** @param {{search: (object|null), onClose: Function}} props  search = recherche ; null = modale fermée. */
const MatchesModal = ({ search, onClose }) => {
  const { t } = useTranslation()
  const [page, setPage] = useState(1)
  const [data, setData] = useState({ matches: [], pagination: null, hasCriteria: true })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [notice, setNotice] = useState(null)
  const [notifying, setNotifying] = useState(false)
  const seq = useRef(0)

  const load = useCallback(async (id, nextPage) => {
    const mine = ++seq.current
    setLoading(true)
    setError(null)
    try {
      const res = await fetchMatches(id, { page: nextPage })
      if (mine !== seq.current) return
      setData({
        matches: res.matches || [],
        pagination: res.pagination || null,
        hasCriteria: res.hasCriteria !== false,
      })
    } catch (err) {
      if (mine !== seq.current) return
      setError(t(`searches:${errorKeyFor(parseApiError(err).code)}`))
      setData({ matches: [], pagination: null, hasCriteria: true })
    } finally {
      if (mine === seq.current) setLoading(false)
    }
  }, [t])

  useEffect(() => {
    if (!search) return
    setPage(1)
    setNotice(null)
    load(search.id, 1)
  }, [search, load])

  const goTo = (next) => {
    setPage(next)
    load(search.id, next)
  }

  const notify = async () => {
    if (notifying) return
    setNotifying(true)
    setNotice(null)
    try {
      const res = await notifyMatches(search.id)
      setNotice({
        color: res.notified > 0 ? 'success' : 'info',
        text:
          res.notified > 0
            ? t('searches:matching.notified', { count: res.notified })
            : t('searches:matching.notified_none'),
      })
      await load(search.id, page)
    } catch (err) {
      setNotice({ color: 'danger', text: t(`searches:${errorKeyFor(parseApiError(err).code)}`) })
    } finally {
      setNotifying(false)
    }
  }

  const total = data.pagination ? data.pagination.total : 0

  return (
    <CModal visible={Boolean(search)} onClose={onClose} alignment="center" size="lg" scrollable>
      <CModalHeader>
        <CModalTitle>{t('searches:matching.modal_title')}</CModalTitle>
      </CModalHeader>
      <CModalBody>
        {search && <p className="fw-semibold mb-1">{search.title}</p>}
        <p className="ms-note">{t('searches:matching.intro')}</p>

        {notice && (
          <CAlert color={notice.color} dismissible onClose={() => setNotice(null)}>
            {notice.text}
          </CAlert>
        )}
        {error && <CAlert color="danger">{error}</CAlert>}

        {loading ? (
          <div className="text-center py-4">
            <CSpinner />
          </div>
        ) : error ? null : !data.hasCriteria ? (
          <div className="ms-empty">{t('searches:matching.empty_no_criteria')}</div>
        ) : data.matches.length === 0 ? (
          <div className="ms-empty">{t('searches:matching.empty')}</div>
        ) : (
          <>
            <p className="small text-medium-emphasis mb-2">
              {t('searches:matching.total', { count: total })}
            </p>
            <ul className="ms-matches">
              {data.matches.map((m) => (
                <li key={m.job.id} className="ms-match">
                  <div className="ms-match-head">
                    <Link to={`/jobs/${m.job.id}`} className="ms-match-title" onClick={onClose}>
                      {m.job.title}
                    </Link>
                    {m.alreadyNotified && (
                      <CBadge color="secondary">{t('searches:matching.already_notified')}</CBadge>
                    )}
                  </div>
                  <div className="small text-medium-emphasis">
                    {[m.job.company, m.job.type, m.job.location].filter(Boolean).join(' · ')}
                  </div>
                  <div className="ms-chips">
                    {describeReasons(m.reasons).map((r) => (
                      <span
                        key={r.key}
                        className={`ms-chip ${r.tone === 'unknown' ? 'ms-chip-unknown' : 'ms-chip-match'}`}
                      >
                        {t(`searches:${r.key}`)}
                      </span>
                    ))}
                  </div>
                </li>
              ))}
            </ul>
            <SearchPager pagination={data.pagination} onPage={goTo} />
          </>
        )}
      </CModalBody>
      <CModalFooter className="ms-matches-foot">
        <p className="ms-note mb-0">{t('searches:matching.notify_hint')}</p>
        <div className="d-flex gap-2">
          <CButton color="secondary" variant="outline" onClick={onClose}>
            {t('searches:actions.back')}
          </CButton>
          <CButton
            color="primary"
            disabled={notifying || loading || Boolean(error) || total === 0}
            onClick={notify}
          >
            {notifying && <CSpinner size="sm" className="me-2" />}
            {t('searches:matching.notify')}
          </CButton>
        </div>
      </CModalFooter>
    </CModal>
  )
}

export default MatchesModal
